import { memo } from "react";

import { Transition } from "@headlessui/react";

import { Button } from "@components/Button";
import { Icon } from "@components/Icon";
import { Text } from "@components/Text";
import {
  STOREFRONT_TRANSITION_DURATION,
  STOREFRONT_TRANSITION_EASING,
} from "@root/tablescape/constants";
import { ThemeMode, type ReviewItemBlock } from "@root/tablescape/state";
import { ReviewBlock } from "@sections/ProductHero/ProductHeroAccordion/ReviewsAccordionItem/ReviewItemBlock.view";
import { type TTailwindString, tw } from "classnames";

const styles = {
  container: tw(
    "w-full",
    "table",
    "table-fixed",
    "border-0",
    "border-solid",
    "border-b",
    "border-divider",
    "text-background-primary-inverse"
  ),
  accordionButton: tw(
    "w-full",
    "flex",
    "items-center",
    "justify-between",
    "px-0",
    "py-6",
    "h-[60px]" as TTailwindString,
    "cursor-pointer",
    "border-0",
    "bg-transparent",
    "hover:no-underline"
  ),
  buttonText: tw("flex-1", "text-left", "text-background-primary-inverse"),
  icon: tw("w-7", "h-7", "text-background-primary-inverse"),
  transition: {
    className: tw(
      "grid",
      "transition-all",
      STOREFRONT_TRANSITION_DURATION,
      STOREFRONT_TRANSITION_EASING
    ),
    enterFrom: tw("grid-rows-collapse", "opacity-0", "invisible"),
    enterTo: tw("grid-rows-expand", "opacity-100", "visible", "pb-6"),
    leaveFrom: tw("grid-rows-expand", "opacity-100"),
    leaveTo: tw("grid-rows-collapse", "opacity-0"),
  },
  content: tw("min-h-0", "text-background-primary-inverse"),
  reviewBlock: (isLastElement: boolean) =>
    tw("pb-4", {
      [tw("border-0", "border-b", "border-solid", "border-divider", "mb-4")]:
        !isLastElement,
    }),
  reviewSectionButton: tw("mt-2"),
};

type Props = {
  buttonText: string;
  className?: TTailwindString | undefined;
  heading: string;
  isOpen: boolean;
  onClick: () => void;
  reviews: ReviewItemBlock[];
  reviewsSectionLinkHandler: () => void;
  showReadMoreButton: boolean;
  theme: ThemeMode;
};

const ReviewsAccordionItemViewBase = ({
  buttonText,
  className,
  heading,
  isOpen,
  onClick,
  reviews,
  reviewsSectionLinkHandler,
  showReadMoreButton,
  theme,
}: Props) => {
  const resolvedContainerStyles = tw(className, styles.container);
  return (
    <>
      <div className={resolvedContainerStyles}>
        <Button
          className={styles.accordionButton}
          fluid={true}
          onClick={onClick}
          type="tertiary"
        >
          <Text as="title2" className={styles.buttonText}>
            {heading}
          </Text>
          <Icon className={styles.icon} name={isOpen ? "minus" : "plus"} />
        </Button>
        <Transition show={isOpen} {...styles.transition}>
          <div className={styles.content}>
            {reviews.map((item: ReviewItemBlock, index) => {
              return (
                <ReviewBlock
                  className={styles.reviewBlock(index === reviews.length - 1)}
                  key={`${index}-${item.title}`}
                  {...item}
                />
              );
            })}
            {showReadMoreButton && (
              <Button
                className={styles.reviewSectionButton}
                fluid={true}
                onClick={reviewsSectionLinkHandler}
                type={theme === ThemeMode.LIGHT ? "secondary" : "secondary-alt"}
              >
                {buttonText}
              </Button>
            )}
          </div>
        </Transition>
      </div>
    </>
  );
};

export const ReviewsAccordionItemView = memo(ReviewsAccordionItemViewBase);
